import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBusiness } from '../contexts/BusinessContext';
import {
  Container,
  Typography,
  Box,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Stack,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../contexts/ThemeModeContext';
import { dataGridHeaderSx } from '../theme/themes';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DevicesIcon from '@mui/icons-material/Devices';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AddBusinessDialog from '../components/AddBusinessDialog';

interface Address {
  id?: string;
  street?: string;
  city?: string;
  state?: string;
  postCode?: string;
  country?: string;
}

interface Business {
  id: string;
  name: string;
  abn: string;
  address?: Address;
  dateCreated: string;
  archivedDate?: string;
  deviceCount: number;
}

const Businesses: React.FC = () => {
  const auth = useAuth();
  const { refreshBusinesses } = useBusiness();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mode } = useThemeMode();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Business | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  const isAdmin = auth.hasRole('DMO_ADMIN');

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/businesses', {
        headers: {
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch businesses');
      }

      const data = await response.json();
      setBusinesses(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatLocation = (address?: Address) => {
    if (!address) return 'No address';
    const parts = [];
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.country) parts.push(address.country);
    return parts.length > 0 ? parts.join(', ') : 'No address';
  };

  const handleAddBusinessSuccess = () => {
    fetchBusinesses();
    // This page keeps its own list independent of BusinessContext (used by
    // RequireActiveSubscription and the business switcher) — without this,
    // a newly created business wouldn't be selectable/gated correctly until
    // the next login re-fetched that context from scratch.
    refreshBusinesses();
  };

  const openDeleteDialog = (business: Business) => {
    setDeleteError(null);
    setDeleteTarget(business);
  };

  // Errors stay in the dialog: the page-level `error` replaces the whole
  // list, which is wrong for a delete that just failed.
  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const response = await fetch(`/api/businesses/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${auth.tokens?.accessToken}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Failed to delete business');
      }
      setBusinesses((prev) => prev.filter((b) => b.id !== deleteTarget.id));
      setDeleteTarget(null);
      refreshBusinesses();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete business');
    } finally {
      setDeleting(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Business Name',
      flex: 1,
      minWidth: 220,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography variant="body2" fontWeight="medium">
            {params.row.name}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'abn',
      headerName: 'ABN',
      width: 140,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {params.row.abn}
        </Typography>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 220,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LocationOnIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {formatLocation(params.row.address)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'deviceCount',
      headerName: 'Devices',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          icon={<DevicesIcon />}
          label={params.row.deviceCount}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      sortable: false,
      renderCell: (params: GridRenderCellParams) =>
        params.row.archivedDate ? (
          <Chip label="Archived" size="small" color="default" />
        ) : (
          <Chip label="Active" size="small" color="success" />
        ),
    },
    {
      field: 'dateCreated',
      headerName: 'Created',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="caption" color="text.secondary">
          {new Date(params.row.dateCreated).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 155,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/businesses/${params.row.id}`);
            }}
          >
            Details
          </Button>
          {isAdmin && (
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                openDeleteDialog(params.row);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  // ABNs are often typed with spaces ("99 598 811 877"), so they're
  // compared with whitespace stripped from both sides.
  const search = searchText.trim().toLowerCase();
  const abnSearch = search.replace(/\s/g, '');
  const filteredBusinesses = businesses.filter((business) =>
    !search
    || business.name.toLowerCase().includes(search)
    || (abnSearch !== '' && (business.abn ?? '').replace(/\s/g, '').includes(abnSearch))
    || (business.address != null && formatLocation(business.address).toLowerCase().includes(search))
  );

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" disableGutters={isMobile} sx={isMobile ? { px: 1 } : undefined}>
      <Box sx={{ mt: 0, mb: { xs: 2, sm: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            mb: { xs: 1.5, sm: 3 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 1, fontSize: 32 }} />
            <Typography variant="h5" component="h3">
              Businesses
            </Typography>
          </Box>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/businesses')}
            >
              Manage Businesses
            </Button>
          )}
        </Box>

        {businesses.length > 0 && (
          <TextField
            label="Search businesses"
            placeholder="Name, ABN or location"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            fullWidth={isMobile}
            sx={{ mb: { xs: 1.5, sm: 3 }, minWidth: { sm: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'action.active' }} />
                </InputAdornment>
              ),
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchText('')} aria-label="Clear search">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        )}

        {businesses.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No businesses found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {isAdmin ? 'Click "Manage Businesses" to add businesses' : 'Add your business to get started'}
            </Typography>
            {!isAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
              >
                Add My Business
              </Button>
            )}
          </Paper>
        ) : filteredBusinesses.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No businesses match "{searchText.trim()}"
            </Typography>
          </Paper>
        ) : isMobile ? (
          <Stack spacing={1.5}>
            {filteredBusinesses.map((business) => (
              <Card key={business.id} variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/businesses/${business.id}`)}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="subtitle2" fontWeight={600}>{business.name}</Typography>
                    </Box>
                    {business.archivedDate ? (
                      <Chip label="Archived" size="small" color="default" />
                    ) : (
                      <Chip label="Active" size="small" color="success" />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block' }}>
                    ABN {business.abn}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">{formatLocation(business.address)}</Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between' }}>
                  <Chip icon={<DevicesIcon />} label={business.deviceCount} size="small" color="primary" variant="outlined" />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button size="small" onClick={(e) => { e.stopPropagation(); navigate(`/businesses/${business.id}`); }}>
                      Details
                    </Button>
                    {isAdmin && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); openDeleteDialog(business); }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </CardActions>
              </Card>
            ))}
          </Stack>
        ) : (
          <Paper sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={filteredBusinesses}
              columns={columns}
              pageSizeOptions={[10, 25, 50, 100]}
              rowHeight={64}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25 },
                },
                sorting: {
                  sortModel: [{ field: 'name', sort: 'asc' }],
                },
              }}
              onRowClick={(params: GridRowParams) => navigate(`/businesses/${params.row.id}`)}
              sx={{
                ...dataGridHeaderSx(mode),
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                },
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                  paddingTop: 1,
                  paddingBottom: 1,
                },
              }}
              disableRowSelectionOnClick
            />
          </Paper>
        )}
      </Box>

      <AddBusinessDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={handleAddBusinessSuccess}
      />
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Business</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
          </Typography>
          <Typography sx={{ mt: 1 }} color="text.secondary" variant="body2">
            {deleteTarget && deleteTarget.deviceCount > 0
              ? `Its ${deleteTarget.deviceCount} device${deleteTarget.deviceCount !== 1 ? 's' : ''} will not be deleted — just unlinked from this business, along with all their events, settings, and images.`
              : 'No devices are linked to this business.'}
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteConfirmed} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Businesses;
